/**
 * DensityMenu — replaces the previous cycle-through density
 * toggle in the shared Table toolbar.
 *
 * The old control cycled xs → md → xl → xs on every click; users
 * couldn't discover the available sizes and had to guess how many
 * more clicks would get them where they wanted. This popover lists
 * all three sizes with the active one marked, matching the
 * `ColumnsMenu` pattern next to it so the toolbar reads
 * consistently.
 */
import { useState } from 'react';
import { Popover } from '../Popover';
import TableToolbarButton from './TableToolbarButton';
import { DensityIcon } from './icons';

const OPTIONS = [
  { id: 'xs', label: 'Compact' },
  { id: 'md', label: 'Comfortable' },
  { id: 'xl', label: 'Spacious' },
];

const CheckIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DensityMenu = ({ density = 'md', onChange, title = 'Row density' }) => {
  const [open, setOpen] = useState(false);

  const handlePick = id => {
    if (id !== density) onChange?.(id);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      placement="bottom-end"
      trigger={
        <TableToolbarButton
          icon={<DensityIcon />}
          title={title}
          active={open}
          aria-label={title}
          aria-haspopup="menu"
          onClick={() => setOpen(o => !o)}
        />
      }
    >
      <div
        className="qhs-pop__head"
        style={{ display: 'flex', alignItems: 'center', padding: '8px 12px 6px' }}
      >
        <h5 style={{ margin: 0, fontSize: 12.5, fontWeight: 700 }}>{title}</h5>
      </div>
      <div role="menu" style={{ padding: '2px 0 6px', minWidth: 180 }}>
        {OPTIONS.map(opt => {
          const selected = opt.id === density;
          return (
            <button
              key={opt.id}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => handlePick(opt.id)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                border: 'none',
                background: selected ? 'var(--qhs-surface-hover)' : 'transparent',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                fontSize: 13,
                color: selected ? 'var(--qhs-brand)' : 'var(--qhs-text-1)',
                fontWeight: selected ? 600 : 400,
              }}
              onMouseEnter={e => {
                if (!selected) {
                  e.currentTarget.style.background = 'var(--qhs-bg)';
                }
              }}
              onMouseLeave={e => {
                if (!selected) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span style={{ width: 14, display: 'inline-flex', color: 'var(--qhs-brand)' }}>
                {selected ? <CheckIcon /> : null}
              </span>
              <span>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </Popover>
  );
};

export default DensityMenu;
